import styled from '@emotion/styled';
import Grid from '@react-css/grid';
import { SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import useAlertQueue from 'hooks/useAlertQueue';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompany } from 'store/selectors/me/company';
import { updateShopifySettings } from 'store/slices/company';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Select from 'storybookConfig/stories/molecules/Select';
import type { ReactSelectOption } from 'types/general';
import { CommerceType } from 'types/models/company';
import type { FraudHoldRiskLevel } from 'types/models/company';

const StyledGrid = styled(Grid)`
  margin-top: 16px;
  gap: 8px;
`;

const StyledField = styled.div`
  margin: 16px 0;
  max-width: 360px;
`;

const RISK_LEVEL_OPTIONS: ReactSelectOption<FraudHoldRiskLevel>[] = [
  { value: '', label: 'Off — do not hold fraud orders' },
  { value: 'MEDIUM', label: 'Medium and above' },
  { value: 'HIGH', label: 'High only' },
];

interface EditFraudHoldSettingsFormProps {
  fraudHoldRiskLevel: ReactSelectOption<FraudHoldRiskLevel>;
}

const EditFraudHoldSettings = () => {
  const dispatch = useDispatch();
  const { addSuccessAlert, addErrorAlert } = useAlertQueue();

  const company = useSelector(selectCompany);

  const currentLevel: FraudHoldRiskLevel = company?.shopify?.fraudHoldRiskLevel ?? '';

  const { handleSubmit, control } = useForm<EditFraudHoldSettingsFormProps>({
    defaultValues: {
      fraudHoldRiskLevel:
        RISK_LEVEL_OPTIONS.find((option) => option.value === currentLevel) ?? RISK_LEVEL_OPTIONS[0],
    },
  });

  const onSubmit = handleSubmit((data: EditFraudHoldSettingsFormProps) => {
    dispatch(updateShopifySettings({ fraudHoldRiskLevel: data.fraudHoldRiskLevel.value })).then(
      (action: { error?: unknown }) => {
        if (action.error) {
          addErrorAlert('Failed to update settings', '');
          return;
        }
        addSuccessAlert('Settings updated successfully', '');
      }
    );
  });

  // Only relevant for retailers on Shopify.
  if (company?.commerceType !== CommerceType.Buyer) return null;
  if (!company?.shopify) return null;

  return (
    <StyledGrid>
      <form onSubmit={onSubmit}>
        <SettingsSectionTitle>Hold high-risk (fraud) orders</SettingsSectionTitle>
        <p className="m-0 mb-2">
          Orders Shopify assesses at or above the selected risk level are held for your review
          instead of being sent to your suppliers. You can then release or reject each held order.
        </p>
        <StyledField>
          <Controller
            name="fraudHoldRiskLevel"
            control={control}
            render={({ field: { ref, ...restField } }) => (
              <Select
                innerRef={ref}
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...restField}
                isClearable={false}
                isFullWidth
                options={RISK_LEVEL_OPTIONS}
              />
            )}
          />
        </StyledField>
        <SecondaryButton size="small" type="submit">
          Save
        </SecondaryButton>
      </form>
    </StyledGrid>
  );
};

export default EditFraudHoldSettings;
